import { forwardRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";
import CircleClearSvg from "@assets/images/ico_circle_clear.svg";
import AlertSvg from "@assets/images/ico_alert.svg";

type ClearButtonType = {
  onClick: VoidFunction;
  isShow: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;

const ClearButton = ({ onClick, isShow, ...props }: ClearButtonType) => {
  return (
    <Button
      variant={"icon"}
      size={"icon"}
      onClick={onClick}
      className={cn("absolute right-4 size-[1.125rem] cursor-pointer opacity-0 peer-focus:opacity-100", !isShow && "hidden")}
      {...props}
    >
      <img src={CircleClearSvg} alt="clear button" />
    </Button>
  );
};

const placeholder = {
  id: "넥슨ID (아이디 또는 이메일)",
  password: "비밀번호",
  oneTimeLogin: "4자리",
};

type Props = {
  value: string;
  setValue: (value: string) => void;
  type?: "id" | "password" | "oneTimeLogin";
  isError?: boolean;
  errorMessage?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

const LoginInput = forwardRef<HTMLInputElement, Props>(
  ({ value, setValue, className, type = "id", isError = false, errorMessage, ...props }, ref) => {
    let inputType;
    switch (type) {
      case "password":
      case "oneTimeLogin":
        inputType = "password";
        break;
      default:
        inputType = "text";
    }

    const handleChange = (val: string) => {
      const trimmedVal = val.trim();
      setValue(trimmedVal);
    };

    const onClear = () => {
      handleChange("");
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e.target.value);
    };

    return (
      <label className={className}>
        <div className="relative flex items-center">
          <Input
            ref={ref}
            value={value}
            type={inputType}
            placeholder={placeholder[type]}
            border={isError ? "error" : "normal"}
            onChange={onChange}
            className="peer appearance-none pr-10"
            {...props}
          />
          {isError && <img src={AlertSvg} alt="alert" className="absolute right-4 peer-focus:hidden" />}
          <ClearButton isShow={!!value && value.trim().length > 0} onClick={onClear} tabIndex={-1} />
        </div>
        <p className={cn("text-red-600 text-sm mt-1", !isError && "hidden")}>{isError && errorMessage}</p>
      </label>
    );
  },
);

export default LoginInput;
